import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Loader2, RefreshCw, Trash2, Upload, Zap } from "lucide-react";
import GlowCard from "../components/GlowCard";
import StatusBadge from "../components/StatusBadge";
import ConfirmDialog from "../StandardsLibrary/ConfirmDialog";
import {
  ParseProgressReport,
  deleteDeliverable,
  deliverableFileUrl,
  listDeliverables,
  parseDeliverable,
  parseProgress,
  uploadDeliverables,
  uploadProgress,
} from "./api";
import { ProjectDeliverable, ProjectStats, VerityProject } from "./types";

interface ProjectDeliverablesProps {
  project: VerityProject;
  /** Reports the deliverable counts up: they are what the project status is derived from. */
  onStats?: (stats: ProjectStats) => void;
  /** Open an already-parsed deliverable in Document AI — the ⚡ once it has gone green. */
  onAdvance?: (deliverable: ProjectDeliverable) => void;
  /**
   * Watching, not working.
   *
   * An administrator sets a project up and follows it; the reviewer assigned to it submits and
   * parses the documents. Two people able to upload against one project is how a pack gets
   * submitted twice and reviewed once.
   */
  readOnly?: boolean;
}

const ACCEPT = ".pdf,.dwg,.dxf,.docx,.doc,.xlsx,.xls,.png,.jpg,.jpeg,.tif,.tiff";

/**
 * 80 MB, matching the server's own ceiling — enforced here so the rejection is instant.
 *
 * It was 20 MB, inherited from what the prototype told suppliers, and that was below the size of a
 * real submission: the reference civil + landscape pack is 35 MB and was refused at the drop zone
 * before anything ever saw it. A client-side limit stricter than the server's rejects work the
 * system can actually handle.
 */
const MAX_BYTES = 80 * 1024 * 1024;

/** Rounded, never precise: "about 20 sec" is honest about an estimate in a way "18 sec" is not.
 *  Matches the Compliance Engine, which shows the same kind of number for the same reason. */
function formatEta(seconds: number): string {
  // CAPPED AT 55, NEVER 60. Rounding to the nearest five turned 59 seconds into "60 sec", so the
  // countdown read "1 min" and then "60 sec" — the same length twice, in two different units, which
  // looks like a fault rather than a tick.
  if (seconds < 60) return `${Math.min(55, Math.max(5, Math.round(seconds / 5) * 5))} sec`;
  return `${Math.round(seconds / 60)} min`;
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

const OCR_TONE: Record<string, "pass" | "warning" | "fail"> = {
  good: "pass",
  fair: "warning",
  poor: "fail",
};

const ANALYSIS_TONE: Record<string, "pass" | "warning" | "fail" | "info"> = {
  pending: "info",
  running: "info",
  complete: "pass",
  failed: "fail",
};

/**
 * Deliverables Ingestion — the first stage of a project.
 *
 * The prototype's screen with its top-right "Upload Deliverables" button removed: the drop zone is
 * the way in, and two controls doing the same thing was one too many. The project header and the
 * step tracker live in ProjectWorkspace above this.
 *
 * OCR Quality and Analysis are shown as "—" until Document AI writes them. Deliberate: a column that
 * says "Good" before anything has read the file is the module lying about its own work.
 */
const ProjectDeliverables = ({ project, onStats, onAdvance, readOnly = false }: ProjectDeliverablesProps) => {
  const [rows, setRows] = useState<ProjectDeliverable[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  /** How much of the upload has left the browser, 0-100. Reaching 100 means sent, not saved. */
  const [sent, setSent] = useState(0);
  /** How much the SERVER has written to storage, 0-100. Null while it has nothing to report. */
  const [stored, setStored] = useState<number | null>(null);
  /** Where the running parse has got to, as the server reports it. */
  const [parseAt, setParseAt] = useState<ParseProgressReport | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [parsingId, setParsingId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<ProjectDeliverable | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /** Held in a ref so reporting the counts never re-triggers the load that produced them. */
  const report = useRef(onStats);
  report.current = onStats;

  /** The three numbers deriveProjectProgress needs, counted off the rows we already hold. */
  const tally = (items: ProjectDeliverable[]): ProjectStats => ({
    deliverables: items.length,
    parsed: items.filter((d) => d.analysis === "complete").length,
    reviewed: items.filter((d) => Boolean(d.humanReviewedAt)).length,
  });

  const load = useCallback(async () => {
    try {
      setError(null);
      const items = await listDeliverables(project.id);
      setRows(items);
      report.current?.(tally(items));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the deliverables.");
      setRows([]);
      report.current?.(tally([]));
    }
  }, [project.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (files: File[]) => {
    if (files.length === 0 || uploading) return;

    const tooBig = files.find((f) => f.size > MAX_BYTES);
    if (tooBig) {
      setError(`${tooBig.name} is larger than 80 MB.`);
      return;
    }

    setUploading(true);
    setSent(0);
    setStored(null);
    setError(null);

    // Named before it starts, because the POST cannot report on itself and the server has nothing
    // to key progress on otherwise.
    const uploadId =
      typeof crypto?.randomUUID === "function" ? crypto.randomUUID() : String(Date.now());

    // THE PART WORTH WATCHING. Sending into server memory is quick and `xhr.upload` covers it;
    // writing to blob storage afterwards is the wait, and only the server can see it. Polling
    // starts as soon as the browser's own send is done and stops when the POST answers.
    let polling: ReturnType<typeof setInterval> | undefined;

    try {
      polling = setInterval(() => {
        void uploadProgress(project.id, uploadId)
          .then((p) => {
            // An unknown id means the write has not begun, or the poll met an instance that is not
            // doing it. Either way there is nothing to say, so the spinner stands.
            if (p && p.total > 0) setStored(Math.round((p.loaded / p.total) * 100));
          })
          .catch(() => {
            /* A missed poll is not a failed upload. The next one answers. */
          });
      }, 600);

      const created = await uploadDeliverables(
        project.id,
        files,
        (fraction) => setSent(Math.round(fraction * 100)),
        uploadId,
      );
      setRows((prev) => {
        const next = [...created, ...(prev ?? [])];
        report.current?.(tally(next));
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "The upload failed.");
    } finally {
      if (polling) clearInterval(polling);
      setUploading(false);
      setStored(null);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    void submit(Array.from(e.dataTransfer.files ?? []));
  };

  /** Runs Document AI over one deliverable and swaps the returned row in. */
  const parse = async (deliverable: ProjectDeliverable) => {
    if (parsingId) return;
    setParsingId(deliverable.id);
    setParseAt(null);
    setError(null);

    // WHAT IT IS DOING AND HOW LONG IS LEFT, from the server rather than from the file's size.
    // Only one of a parse's four steps is predictable from megabytes — the reading — and it is the
    // smallest part of the wait. The transfers either side of it are the rest, and they vary by
    // more than a factor of two on the same machine, so a countdown computed here would be a
    // confident number about the part that behaves.
    let polling: ReturnType<typeof setInterval> | undefined;

    try {
      polling = setInterval(() => {
        void parseProgress(project.id, deliverable.id)
          .then((p) => {
            if (p && p.stage !== "done") setParseAt(p);
          })
          .catch(() => {
            /* A missed poll is not a failed parse. */
          });
      }, 700);

      // No artificial delay: reading a drawing pack takes seconds of real work, so the spinner has
      // something to cover. It used to be padded to two seconds because the stamp it wrote was
      // instant.
      const updated = await parseDeliverable(project.id, deliverable.id);
      setRows((prev) => {
        const next = (prev ?? []).map((r) => (r.id === updated.id ? updated : r));
        report.current?.(tally(next));
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "The parse failed.");
    } finally {
      if (polling) clearInterval(polling);
      setParsingId(null);
      setParseAt(null);
    }
  };

  const remove = async (deliverable: ProjectDeliverable) => {
    setBusyId(deliverable.id);
    setError(null);
    try {
      await deleteDeliverable(project.id, deliverable.id);
      setRows((prev) => {
        const next = (prev ?? []).filter((r) => r.id !== deliverable.id);
        report.current?.(tally(next));
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove the deliverable.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      {!readOnly && (
      <div
        className={`vr-drop ${dragging ? "vr-dragging" : ""} ${uploading ? "vr-drop-busy" : ""}`}
        onClick={() => !uploading && fileInput.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        {uploading ? (
          <Loader2 size={26} className="vr-drop-icon vr-spin-inline" />
        ) : (
          <Upload size={26} className="vr-drop-icon" />
        )}
        {/* TWO TRANSFERS, AND THE SECOND IS THE ONE THAT TAKES THE TIME.
            Sending into the server's memory is quick, and `xhr.upload` measures it. Writing that to
            blob storage afterwards is where a 35 MB pack actually waits, and only the server can see
            it — so once the browser's send is done the bar switches to what the server reports.
            If the server says nothing, the wording stops claiming a number. */}
        <span className="vr-drop-title">
          {!uploading
            ? "Drag and drop engineering deliverables or click to browse"
            : sent < 100
              ? `Submitting… ${sent}%`
              : stored !== null
                ? `Saving to storage… ${stored}%`
                : "Saving to storage…"}
        </span>
        {uploading && (sent < 100 || stored !== null) && (
          <span className="vr-drop-track">
            <span
              className="vr-drop-fill"
              style={{ width: `${sent < 100 ? sent : (stored ?? 0)}%` }}
            />
          </span>
        )}
        <span className="vr-drop-sub">
          Supports PDF, DWG, DXF, DOCX, XLSX, and scanned drawings (max 80MB)
        </span>
        <input
          ref={fileInput}
          type="file"
          multiple
          accept={ACCEPT}
          style={{ display: "none" }}
          onChange={(e) => {
            void submit(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />
      </div>
      )}

      {error && <div className="vr-form-error">{error}</div>}

      <GlowCard className="vr-table-card" hover={false}>
        <div className="vr-table-head">
          <h3>Submitted Deliverables ({rows?.length ?? 0})</h3>
          <button className="vr-rowbtn" onClick={() => void load()}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        <div className="vr-table-scroll">
          <table className="vr-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Type</th>
                <th>Discipline</th>
                <th>Size</th>
                <th>Date</th>
                <th>OCR Quality</th>
                <th>Analysis</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(rows ?? []).map((row) => {
                const busy = busyId === row.id;
                const parsing = parsingId === row.id;
                const parsed = Boolean(row.extraction);
                return (
                  <tr key={row.id}>
                    <td className="vr-cell-title">
                      <div className="vr-doc-cell">
                        <FileText size={14} />
                        <a
                          href={deliverableFileUrl(project.id, row.id)}
                          target="_blank"
                          rel="noreferrer"
                          title={row.fileName}
                        >
                          <strong>{row.fileName}</strong>
                        </a>
                      </div>
                    </td>
                    <td className="vr-cell-dim">{row.docType}</td>
                    <td className="vr-cell-dim">{row.discipline || "—"}</td>
                    <td className="vr-cell-dim">{formatSize(row.fileSize)}</td>
                    <td className="vr-cell-dim">{formatDate(row.uploadedAt)}</td>
                    <td>
                      {row.ocrQuality ? (
                        <StatusBadge
                          status={OCR_TONE[row.ocrQuality]}
                          label={row.ocrQuality[0].toUpperCase() + row.ocrQuality.slice(1)}
                        />
                      ) : (
                        <span className="vr-cell-dim">—</span>
                      )}
                    </td>
                    <td>
                      {row.analysis ? (
                        // A failed parse carries its reason on hover. A bare "Failed" tells a
                        // reviewer to retry something that will fail the same way every time —
                        // "No reader for Spreadsheet files yet" tells them not to bother.
                        <span title={row.analysisError ?? undefined}>
                          <StatusBadge
                            status={ANALYSIS_TONE[row.analysis]}
                            label={row.analysis[0].toUpperCase() + row.analysis.slice(1)}
                          />
                        </span>
                      ) : (
                        <span className="vr-cell-dim">—</span>
                      )}
                    </td>
                    <td>
                      <div className="vr-cell-actions">
                        {/* Marks, not captions — what each one does lives on hover. The bolt runs
                            Document AI; once it has, it goes green and becomes the way through to
                            the extraction it produced. */}
                        {/* Watching an unparsed document offers nothing — the bolt is the act of
                            parsing, and an administrator does not do that. A parsed one still opens
                            in Document AI, because reading the result is the point of watching. */}
                        {(!readOnly || parsed) && (
                          <button
                            className={`vr-rowbtn vr-icononly${parsed ? " vr-parsed" : ""}`}
                            onClick={() => (parsed ? onAdvance?.(row) : void parse(row))}
                            disabled={busy || (readOnly && !parsed)}
                            title={parsed ? "Parsed — open in Document AI" : "Click to Parse"}
                            aria-label={parsed ? "Open in Document AI" : "Parse"}
                          >
                            {parsing ? <Loader2 size={13} className="vr-spin-inline" /> : <Zap size={13} />}
                          </button>
                        )}
                        {/* Beside the bolt, not in place of it — the spinner says something is
                            happening and this says what, and for how much longer. */}
                        {parsing && parseAt && (
                          <span className="vr-parse-eta" title={parseAt.detail}>
                            <span className="vr-parse-eta-step">{parseAt.detail}</span>
                            {parseAt.etaSeconds !== null && parseAt.etaSeconds > 0 && (
                              <span className="vr-parse-eta-left">
                                about {formatEta(parseAt.etaSeconds)} left
                              </span>
                            )}
                          </span>
                        )}
                        {!readOnly && (
                          <button
                            className="vr-rowbtn vr-icononly vr-danger"
                            onClick={() => setConfirming(row)}
                            disabled={busy}
                            title="Remove this deliverable"
                            aria-label="Remove deliverable"
                          >
                            {busy ? <Loader2 size={13} className="vr-spin-inline" /> : <Trash2 size={13} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {rows === null && (
          <div className="vr-empty">
            <Loader2 size={16} className="vr-spin-inline" /> Loading deliverables…
          </div>
        )}
        {rows !== null && rows.length === 0 && (
          <div className="vr-empty">
            Nothing submitted against this project yet. Drop the supplier's documents above to begin.
          </div>
        )}
      </GlowCard>

      {confirming && (
        <ConfirmDialog
          title="Remove this deliverable?"
          tone="danger"
          confirmLabel="Remove permanently"
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            const target = confirming;
            setConfirming(null);
            void remove(target);
          }}
        >
          <p className="vr-confirm-lead">
            <strong>{confirming.fileName}</strong>
          </p>
          <p>
            This deletes the submitted file and its record. Anything derived from it would have to be
            produced again from a fresh submission.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
};

export default ProjectDeliverables;
